import React from 'react';
import _, { isArray } from 'lodash';
import actions from 'store/user-management/actions';
import { getStore } from 'store/storeRef';
import {
  FIELD_REQUEST_API,
  getMfaBulkChangeCompanyName,
} from 'domain/user-management/consts';
import {
  AGENT_FLAG,
  AGENCY_ID,
  CHARGE_NAME,
  COMPANY_NAME,
  EMAIL,
  MFA_ENABLED,
  USER_ID,
  USER_PASSWORD,
  USER_ROLES,
  USER_TYPE,
  REGIST_DATE,
  UPDATE_DATE,
} from 'domain/fields';
import {
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
} from 'domain/settings/display-items';
import { getErrorMessageByCode, renderUserType } from 'services/utils';
import * as messageError from 'services/validations/messageErrorByCode';
import {
  ROLE_CAPI_AGENT_SETTING,
  ROLE_CAPI_SETTING,
  ROLE_EBIS_API,
  ROLE_LINE_ADD_FRIEND_SETTING,
  ROLE_MANAGE_ACCOUNT,
  ROLE_MMM_ANALYSIS,
  ROLE_SEARCH_CONSOLE_SETTING,
  ROLE_SETTING,
  ROLE_SETTING_MEDIA_SYNC,
  ROLE_SETTING_OTHER_AGENT,
  ROLE_VIEW_AD,
  ROLE_VIEW_ALL,
  ROLE_VIEW_ALL_AD_ONLY,
} from 'domain/permissions/roles';
import { IconCautionYellow, IconMfaEnabled } from 'views/atoms/icon/SvgIcon';
import useCheckPermissions from 'services/custom-hooks/useCheckPermissions';
import {
  SETTING_AGENCY_EDIT,
  SETTING_AGENCY_VIEW,
} from 'domain/permissions/contractPermissions';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';

export const userManagementErrorType = {
  invalidPassword: 'invalidPassword',
  unPermissionAgent: 'unPermissionAgent',
  invalidAgent: 'invalidAgent',
  bothWarning: 'bothWarning',
  unPermissionAgentCapicoSingle: 'unPermissionAgentCapicoSingle',
};

export const prepareApiItem = (item) => {
  const apiItem = { ...item };
  delete apiItem[USER_ID];
  if (apiItem[USER_PASSWORD] === undefined) {
    apiItem[USER_PASSWORD] = '';
  }
  return apiItem;
};

export const buildEditPayloadFromUser = (user, overrides = {}) => ({
  [USER_ID]: user.user_id,
  [USER_PASSWORD]: '',
  [COMPANY_NAME]: user.agent_flag ? user.agency_name : user.company_name,
  [CHARGE_NAME]: user.charge_name,
  [EMAIL]: user.email,
  [AGENT_FLAG]: user.agent_flag,
  [AGENCY_ID]: user.agency_id,
  [MFA_ENABLED]: user.mfa_enabled ?? false,
  [USER_ROLES]: user.agent_flag ? [] : user.user_roles,
  ...overrides,
});

class UserManagementService {
  static addNewItem(item, callback) {
    getStore().dispatch(actions.create(item, callback));
  }

  static getUserByUserId(userId, list) {
    if (!isArray(list) || !userId) return null;
    const userSelected = list.find((item) => item.user_id === userId);
    return userSelected || null;
  }

  static changeSelectItemStore(authId) {
    getStore().dispatch(actions.changeSelectedItem(authId));
  }

  static remove(key) {
    getStore().dispatch(actions.remove(key));
  }

  static editItem(targetId, item, callback) {
    const apiItem = prepareApiItem(item);
    getStore().dispatch(actions.update({ targetId, item, apiItem }, callback));
  }

  static bulkUpdateMfaEnabled(users, mfaEnabled, { onSuccess } = {}) {
    getStore().dispatch(actions.bulkUpdateMfa({ users, mfaEnabled }, onSuccess));
  }
}

export const formatErrorMessage = (errors = [], labels = {}) => {
  if (!isArray(errors)) return {};
  return errors.reduce((acc, error = { field: '' }) => {
    const field = Object.keys(FIELD_REQUEST_API).find(
      (key) => FIELD_REQUEST_API[key] === error.field
    );
    return {
      ...acc,
      [field]: {
        result: false,
        message: getErrorMessageByCode({ ...error, field }, messageError, {
          label: labels[field],
        }),
      },
    };
  }, {});
};

export const createSettingListUserNormal = (settingList) => {
  return settingList.reduce((acc, item) => {
    if (
      item.permision?.includes(ROLE_SETTING_MEDIA_SYNC) ||
      item.permision?.includes(ROLE_SEARCH_CONSOLE_SETTING) ||
      item.permision?.includes(ROLE_LINE_ADD_FRIEND_SETTING)
    )
      return acc;
    const { subNames, ...rest } = item;
    return [...acc, rest];
  }, []);
};

export const getNotePagination = ({
  hasContractAgent,
  isCapiSingleContract,
  maxUser,
  userUpLimit,
  agentUserUpLimit,
}) => {
  if (hasContractAgent)
    return `/登録上限数${maxUser}件（通常ユーザー${userUpLimit}件・エージェントユーザー${agentUserUpLimit}件)`;

  if (isCapiSingleContract) return `/登録上限数${maxUser}件`;

  return '';
};

export const WarningUserManagement = ({
  onClick,
  type,
  content,
  isCompanyCol = false,
}) => {
  return (
    <div>
      <p
        className={classNames('user-management-warning', {
          'company-col-warning': isCompanyCol,
        })}
      >
        <IconCautionYellow />
        <span>{content}</span>
      </p>
      <Button
        size="sm"
        className="button-user-management-warning"
        onClick={(e) => {
          e.stopPropagation();
          onClick({ type });
        }}
        style={{ display: 'block' }}
      >
        詳細を表示
      </Button>
    </div>
  );
};

export const RenderAgent = ({ user, onClick }) => {
  const isInvalidAgent = !user.agency_name || !user.agency_id;
  if (isInvalidAgent)
    return (
      <WarningUserManagement
        onClick={onClick}
        content="設定エラー"
        type={userManagementErrorType.invalidAgent}
        isCompanyCol
      />
    );
  return <>{user.agency_name}</>;
};

const WarningUserManagementUserId = ({ user, onClick }) => {
  const { checkPermissions } = useCheckPermissions();
  const isPermitted = checkPermissions('allBy', [
    SETTING_AGENCY_EDIT,
    SETTING_AGENCY_VIEW,
  ]);
  const hasPermissionError = !isPermitted;
  const hasPasswordError = user.is_invalid;
  const hasBothWarnings =
    user.agent_flag && hasPermissionError && hasPasswordError;
  const isUserAgent = user.agent_flag;
  const renderWarning = () => {
    if (hasBothWarnings) {
      return (
        <WarningUserManagement
          onClick={onClick}
          type={userManagementErrorType.bothWarning}
          content="設定エラー"
        />
      );
    }
    if (hasPasswordError || (isUserAgent && hasPermissionError)) {
      return (
        <WarningUserManagement
          onClick={onClick}
          type={
            hasPasswordError
              ? userManagementErrorType.invalidPassword
              : userManagementErrorType.unPermissionAgent
          }
          content={hasPasswordError ? '設定エラー' : '権限エラー'}
        />
      );
    }
    return null;
  };

  return (
    <>
      <span
        className={classNames('user-management-id-context', {
          'spacing-bottom': !!renderWarning,
        })}
      >
        {user.user_id}
      </span>
      {renderWarning()}
    </>
  );
};

export const getSelectedUsersForMfaBulkChange = (userList, selectedUserIds) => {
  if (!_.isArray(userList) || !_.isArray(selectedUserIds)) return [];
  return userList.filter((user) => selectedUserIds.includes(user.user_id));
};

export const hasUndeletableSelectedUsers = (
  userList,
  selectedUserIds,
  currentUserId
) => {
  if (!_.isArray(userList) || !_.isArray(selectedUserIds)) return false;

  return userList.some(
    (user) =>
      selectedUserIds.includes(user.user_id) &&
      (user.is_account_owner === true || user.user_id === currentUserId)
  );
};

export const getDeletableUserIds = (
  userList,
  selectedUserIds,
  currentUserId
) => {
  if (!_.isArray(userList) || !_.isArray(selectedUserIds)) return [];

  return userList
    .filter(
      (user) =>
        selectedUserIds.includes(user.user_id) &&
        !user.is_account_owner &&
        user.user_id !== currentUserId
    )
    .map((user) => user.user_id);
};

const getUserTypeSortRank = (user) => {
  if (user?.is_account_owner) return 1;
  if (!user?.agent_flag) return 2;
  return 3;
};

const getSortValue = (user, field) => {
  switch (field) {
    case 'user_id_view':
      return user.user_id || '';
    case COMPANY_NAME:
      return getMfaBulkChangeCompanyName(user) || '';
    case USER_TYPE:
      return getUserTypeSortRank(user);
    case MFA_ENABLED:
      return user.mfa_enabled ? 1 : 0;
    case REGIST_DATE:
      return user.regist_date || '';
    case UPDATE_DATE:
      return user.update_date || '';
    default:
      return user[field] ?? '';
  }
};

const compareSortValues = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b), 'ja');
};

/**
 * Client-side sort for user-management list.
 * Do not introduce paging with this approach: sorting only the current page
 * would break the correct global order.
 */
export const sortUserList = (list, sort = {}) => {
  const { field, direction } = sort;
  if (!_.isArray(list) || !field || !direction) {
    return list;
  }
  if (![SORT_DIRECTION_ASC, SORT_DIRECTION_DESC].includes(direction)) {
    return list;
  }

  const reverse = direction === SORT_DIRECTION_DESC ? -1 : 1;
  const sorted = [...list].sort((left, right) => {
    const primary =
      compareSortValues(getSortValue(left, field), getSortValue(right, field)) *
      reverse;
    if (primary !== 0 || field !== USER_TYPE) {
      return primary;
    }
    // Former API used user_type + regist_date (always ASC as secondary)
    return compareSortValues(
      getSortValue(left, REGIST_DATE),
      getSortValue(right, REGIST_DATE)
    );
  });
  return sorted;
};

export const addNewKeyForDataList = (
  list,
  selectedRows,
  handleClick
) => {
  if (!_.isArray(list)) return [];
  const selected = selectedRows ?? [];

  return list.map((item) => {
    return {
      ...item,
      rowId: item.user_id,
      selected: selected.includes(item.user_id),
      user_type: renderUserType(item),
      user_id_view: (
        <WarningUserManagementUserId user={item} onClick={handleClick} />
      ),
      company_name: item.agent_flag ? (
        <RenderAgent user={item} onClick={handleClick} />
      ) : (
        item.company_name
      ),
      mfa_enabled: item.mfa_enabled ? (
        <>
          <span className="user-management__mfa-enabled_enabled">
            有効
            <IconMfaEnabled />
          </span>
        </>
      ) : (
        '無効'
      ),
      [ROLE_VIEW_ALL]:
        item.user_roles.includes(ROLE_VIEW_ALL) ||
        (item.user_roles.includes(ROLE_VIEW_ALL_AD_ONLY) && item.agent_flag) ? (
          <i className="fas fa-check" />
        ) : null,
      [ROLE_VIEW_AD]: item.user_roles.includes(ROLE_VIEW_AD) ? (
        <i className="fas fa-check" />
      ) : null,
      [ROLE_SETTING]:
        item.user_roles.includes(ROLE_SETTING) ||
        (item.user_roles.includes(ROLE_SETTING_OTHER_AGENT) &&
          item.agent_flag) ? (
          <i className="fas fa-check" />
        ) : null,
      [ROLE_EBIS_API]: item.user_roles.includes(ROLE_EBIS_API) ? (
        <i className="fas fa-check" />
      ) : null,
      [ROLE_CAPI_SETTING]:
        item.user_roles.includes(ROLE_CAPI_SETTING) ||
        item.user_roles.includes(ROLE_CAPI_AGENT_SETTING) ? (
          <i className="fas fa-check" />
        ) : null,
      [ROLE_MMM_ANALYSIS]: item.user_roles.includes(ROLE_MMM_ANALYSIS) ? (
        <i className="fas fa-check" />
      ) : null,
      [ROLE_MANAGE_ACCOUNT]: item.user_roles.includes(ROLE_MANAGE_ACCOUNT) ? (
        <i className="fas fa-check" />
      ) : null,
    };
  });
};

export const WARNING_CONTENT_MAP = {
  [userManagementErrorType.bothWarning]: (
    <>
      ・パスワードポリシーを満たすパスワードへの変更が必要です。編集ボタンをクリックしてパスワードを再設定してください。
      <br />
      ・エージェント権限が付与されていないプランのため、エージェントユーザーとしてログインできません。エージェント権限を付与いただくか、ユーザーを削除し一般ユーザーとして再作成してください。
    </>
  ),
  [userManagementErrorType.unPermissionAgent]: (
    <>
      エージェント権限が付与されていないプランのため、エージェントユーザーとしてログインできません。エージェント権限を付与いただくか、ユーザーを削除し一般ユーザーとして再作成してください。
    </>
  ),
  [userManagementErrorType.invalidAgent]: (
    <>
      エージェントユーザーの場合担当代理店の設定が必要です。編集ボタンをクリックして担当代理店を再設定してください。
    </>
  ),
  [userManagementErrorType.invalidPassword]: (
    <>
      パスワードポリシーを満たすパスワードへの変更が必要です。編集ボタンをクリックしてパスワードを再設定してください。
    </>
  ),
  [userManagementErrorType.unPermissionAgentCapicoSingle]: (
    <>
      エージェント権限が付与されていないプランのため、エージェントユーザーとしてログインできません。ユーザーを削除し、一般ユーザーとして再作成してください。
    </>
  ),
};

export default UserManagementService;
